// BulkActionsBar.jsx
import React from "react";
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Fade, LinearProgress, Menu, MenuItem, Popper, Snackbar, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Typography } from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import {
  pillButtonFilledSx,
  pillButtonInteractiveSx,
  withPillButtonClassName,
} from "../utils/pillButtonStyles";
import { BulkActionsBarContext } from "./BulkActionsBarContext";
import { getDefaultActionButtonStartIcon } from "./ActionButtonIcons";


const PURPLE = "#9C4FFF";
const TEXT = "#344054";
const MUTED = "#667085";
const BORDER = "#EAECF0";
const DISABLED_TEXT = "#B3B8C5";
const SOFT_PURPLE_HOVER_BG = "#F5F3FF";
let activeExternalFeedbackOwnerId = null;

function exportFieldLabel(value = "") {
  return String(value || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .trim();
}

function exportFieldValue(row = {}, fieldId = "") {
  const value = row?.[fieldId];
  if (Array.isArray(value)) return value.map((item) => typeof item === "object" ? JSON.stringify(item) : item).join(" | ");
  if (value && typeof value === "object") return JSON.stringify(value);
  return value ?? "";
}

function exportRowKey(row = {}) {
  return row?.id ?? row?.candidate_id ?? row?.job_id ?? row?.client_id ?? row?.lead_id ?? row?.name ?? "";
}

function exportFieldGroup(fieldId = "") {
  const key = String(fieldId).toLowerCase();
  if (key.includes("stage")) return "Stages";
  if (key.includes("match")) return "Matches";
  if (key.includes("activit") || key.includes("note") || key.includes("timeline")) return "Activities";
  return "Details";
}

function canonicalExportFieldKey(value = "") {
  const key = String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  return key.replace(/^(lead|job|company|client|candidate|contact|content|team)(status|stage|activity)$/, "$2");
}

function uniqueExportFields(fields = []) {
  const keys = new Set();
  return (Array.isArray(fields) ? fields : []).filter((field) => {
    const labelKey = canonicalExportFieldKey(field?.label || field?.id);
    const idKey = canonicalExportFieldKey(field?.id);
    const key = labelKey || idKey;
    if (!key || keys.has(key)) return false;
    keys.add(key);
    return true;
  });
}

function getSelectedItemsText(selectedCount = 0) {
  const count = Number(selectedCount);
  if (Number.isFinite(count) && count > 0) {
    return `${count} selected ${count === 1 ? "item" : "items"}`;
  }
  return "the current selection";
}

function confirmationEnabled(config, { defaultEnabled = true } = {}) {
  if (typeof config?.confirm === "boolean") return config.confirm;
  return defaultEnabled;
}

function buildDefaultConfirmationMessage({
  label = "",
  optionLabel = "",
  selectedCount = 0,
}) {
  const actionText = [label, optionLabel]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" -> ");

  return `Are you sure you want to continue with "${actionText || "this action"}" for ${getSelectedItemsText(selectedCount)}?`;
}

function buildConfirmationDescriptor({
  config,
  selectedCount = 0,
  optionLabel = "",
  defaultEnabled = true,
  onConfirm,
}) {
  if (!confirmationEnabled(config, { defaultEnabled }) || typeof onConfirm !== "function") {
    return null;
  }

  const label = String(config?.confirmActionLabel || config?.label || "").trim();
  const message =
    typeof config?.confirmMessage === "function"
      ? String(config.confirmMessage({ action: config, label, optionLabel, selectedCount }) || "").trim()
      : String(config?.confirmMessage || "").trim();

  return {
    title: String(config?.confirmTitle || "Confirm Bulk Action").trim(),
    message:
      message ||
      buildDefaultConfirmationMessage({
        label,
        optionLabel,
        selectedCount,
      }),
    confirmLabel: String(config?.confirmLabel || "Confirm").trim(),
    cancelLabel: String(config?.cancelLabel || "Cancel").trim(),
    onConfirm,
  };
}

function resolveActionStartIcon(action = null, size = 18) {
  if (!action || typeof action !== "object") {
    return null;
  }

  if (Object.prototype.hasOwnProperty.call(action, "startIcon")) {
    return action.startIcon;
  }

  return getDefaultActionButtonStartIcon(action?.label, size);
}

function BulkMenuAction({ action, actionKey, requestConfirmation, selectedCount = 0, notifySelectionRequired, onRequestExport }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const buttonRef = React.useRef(null);
  const ownerIdRef = React.useRef(`bulk-menu-${Math.random().toString(36).slice(2)}`);
  const disabled = Boolean(action.disabled);
  const open = Boolean(anchorEl);
  const options = Array.isArray(action.options) ? action.options : [];
  const unavailable = disabled || !options.length;
  const selectionMissing = action.requiresSelection !== false && Number(selectedCount) <= 0;
  const disabledMessage = String(action.disabledMessage || "").trim();
  const canShowDisabledMessage = unavailable && Boolean(disabledMessage);
  const actionFeedback =
    action?.feedback && typeof action.feedback === "object" ? action.feedback : null;
  const onFeedbackClose =
    typeof action?.onFeedbackClose === "function" ? action.onFeedbackClose : null;
  const sharedExternalFeedbackOpen = Boolean(actionFeedback?.open && actionFeedback?.message);
  const externalFeedbackOpen = Boolean(
    sharedExternalFeedbackOpen && activeExternalFeedbackOwnerId === ownerIdRef.current
  );
  const visibleFeedback = externalFeedbackOpen
    ? {
        severity: actionFeedback?.severity || "success",
        message: String(actionFeedback?.message || ""),
      }
    : feedbackOpen
    ? {
        severity: "info",
        message: disabledMessage,
      }
    : null;

  React.useEffect(() => {
    if (!feedbackOpen) return undefined;
    const timeout = window.setTimeout(() => {
      setFeedbackOpen(false);
    }, 2500);
    return () => window.clearTimeout(timeout);
  }, [feedbackOpen]);

  React.useEffect(() => {
    if (!externalFeedbackOpen || !onFeedbackClose) return undefined;
    const timeout = window.setTimeout(() => {
      onFeedbackClose();
    }, Number(actionFeedback?.autoHideDuration || 2500));
    return () => window.clearTimeout(timeout);
  }, [actionFeedback?.autoHideDuration, externalFeedbackOpen, onFeedbackClose]);

  React.useEffect(() => {
    if (sharedExternalFeedbackOpen) return undefined;
    if (activeExternalFeedbackOwnerId === ownerIdRef.current) {
      activeExternalFeedbackOwnerId = null;
    }
    return undefined;
  }, [sharedExternalFeedbackOpen]);

  const handleOpen = (event) => {
    if (selectionMissing) {
      notifySelectionRequired?.(action.emptySelectionMessage);
      return;
    }

    if (typeof action.onClick === "function") {
      const shouldOpen = action.onClick(event);
      if (shouldOpen === false) return;
    }

    if (unavailable) {
      if (canShowDisabledMessage) {
        setFeedbackOpen(true);
      }
      return;
    }

    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (option, event) => {
    if (option?.disabled) return;
    event?.persist?.();

    if (/export/i.test(String(option?.label || option?.value || "")) && typeof onRequestExport === "function") {
      handleClose();
      onRequestExport();
      return;
    }

    const executeSelection = () => {
      activeExternalFeedbackOwnerId = ownerIdRef.current;
      if (typeof action.onSelect === "function") {
        return action.onSelect(
          option?.value ?? option?.label ?? option,
          event,
          { confirmedByBulkActionsBar: true }
        );
      }
      return undefined;
    };

    const confirmation = buildConfirmationDescriptor({
      config: action,
      selectedCount,
      optionLabel: option?.label || option?.value || "",
      defaultEnabled: true,
      onConfirm: executeSelection,
    });

    if (action.closeOnSelect !== false) {
      handleClose();
    }

    if (confirmation && typeof requestConfirmation === "function") {
      requestConfirmation(confirmation);
      return;
    }

    executeSelection();
  };

  return (
    <>
      <Button
        ref={buttonRef}
        key={actionKey}
        className="bulk-action-flat"
        type="button"
        variant="text"
        disableRipple={Boolean(action.disableRipple ?? false)}
        disabled={!selectionMissing && !canShowDisabledMessage && unavailable}
        onClick={handleOpen}
        aria-disabled={unavailable ? "true" : undefined}
        startIcon={null}
        endIcon={action.endIcon || <KeyboardArrowDownRoundedIcon sx={{ fontSize: 16 }} />}
        sx={{
          minWidth: "auto",
          p: 0,
          px: 0,
          py: 0,
          borderRadius: 999,
          textTransform: "none",
          fontSize: 14,
          fontWeight: 500,
          lineHeight: 1.2,
          color: unavailable ? DISABLED_TEXT : PURPLE,
          background: "transparent !important",
          backgroundColor: "transparent !important",
          border: "none !important",
          borderColor: "transparent !important",
          boxShadow: "none !important",
          justifyContent: "flex-start",
          cursor: canShowDisabledMessage ? "not-allowed" : undefined,
          "&:hover, &.Mui-focusVisible, &:active": {
            background: `${SOFT_PURPLE_HOVER_BG} !important`,
            backgroundColor: `${SOFT_PURPLE_HOVER_BG} !important`,
            border: "none !important",
            borderColor: "transparent !important",
            boxShadow: "none !important",
            color: unavailable ? DISABLED_TEXT : "#7C3AED",
          },
          "&.Mui-disabled": {
            color: DISABLED_TEXT,
            background: "transparent !important",
            backgroundColor: "transparent !important",
            border: "none !important",
            borderColor: "transparent !important",
            boxShadow: "none !important",
          },
          ...(action.sx || {}),
        }}
      >
        {action.label}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: action.menuWidth || 220,
            borderRadius: 2.5,
            boxShadow: "0 16px 40px rgba(15,23,42,0.16)",
            ...(action.menuPaperSx || {}),
          },
        }}
      >
        {options.map((option, index) => {
          const normalizedOption =
            option && typeof option === "object"
              ? option
              : {
                  value: option,
                  label: String(option ?? ""),
                };
          const insetLevel = Math.max(0, Number(normalizedOption.insetLevel || 0));

          if (normalizedOption.type === "divider") {
            return (
              <Divider
                key={normalizedOption.key || `menu-divider-${index}`}
                sx={{ my: 0.5 }}
              />
            );
          }

          if (normalizedOption.type === "label") {
            return (
              <Box
                key={normalizedOption.key || normalizedOption.label || `menu-label-${index}`}
                sx={{
                  px: 2,
                  pt: index === 0 ? 1.25 : 1.5,
                  pb: 0.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: MUTED,
                    letterSpacing: 0,
                    textTransform: "none",
                  }}
                >
                  {normalizedOption.label}
                </Typography>
              </Box>
            );
          }

          return (
            <MenuItem
              key={normalizedOption.key || normalizedOption.value || normalizedOption.label || index}
              disabled={Boolean(normalizedOption.disabled)}
              onClick={(event) => handleSelect(normalizedOption, event)}
              sx={{
                fontSize: 13,
                minHeight: 40,
                pl: 2 + insetLevel * 1.5,
                ...(action.menuItemSx || {}),
                ...(normalizedOption.sx || {}),
              }}
            >
              {normalizedOption.label}
            </MenuItem>
          );
        })}
      </Menu>

      <Popper
        open={Boolean(visibleFeedback?.message && buttonRef.current)}
        anchorEl={buttonRef.current}
        placement="top"
        transition
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, 10],
            },
          },
        ]}
        sx={{ zIndex: (theme) => theme.zIndex.snackbar }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={180}>
            <Alert
              severity={visibleFeedback?.severity || "success"}
              onClose={() => {
                if (externalFeedbackOpen) {
                  if (activeExternalFeedbackOwnerId === ownerIdRef.current) {
                    activeExternalFeedbackOwnerId = null;
                  }
                  onFeedbackClose?.();
                  return;
                }
                setFeedbackOpen(false);
              }}
              sx={{
                width: "max-content",
                maxWidth: 320,
                boxShadow: "0 14px 32px rgba(15,23,42,0.16)",
                "& .MuiAlert-message": {
                  fontSize: 12,
                  lineHeight: 1.35,
                },
              }}
            >
              {visibleFeedback?.message || ""}
            </Alert>
          </Fade>
        )}
      </Popper>
    </>
  );
}

export default function BulkActionsBar({
  actions = [],
  rightButton = null,
  rightButtons = null,
  selectedCount = 0,
  selectionLabel = "record",
  sx = {},
  exportContext = null,
}) {
  const [pendingConfirmation, setPendingConfirmation] = React.useState(null);
  const [confirmBusy, setConfirmBusy] = React.useState(false);
  const [rightButtonFeedback, setRightButtonFeedback] = React.useState(null);
  const [selectionNotice, setSelectionNotice] = React.useState("");
  const [exportDialogOpen, setExportDialogOpen] = React.useState(false);
  const [exportProgress, setExportProgress] = React.useState(0);
  const [exportTab, setExportTab] = React.useState("Table");
  const [previewPage, setPreviewPage] = React.useState(0);
  const exportFields = React.useMemo(() => {
    const configuredFields = Array.isArray(exportContext?.fields) && exportContext.fields.length
      ? exportContext.fields
      : Array.isArray(exportContext?.columns) ? exportContext.columns : [];
    const detailFields = Array.from(
      new Set(
        (exportContext?.rows || []).flatMap((row) =>
          row && typeof row === "object" ? Object.keys(row) : []
        )
      )
    )
      .filter((id) => !["id", "actions", "info"].includes(String(id).toLowerCase()))
      .map((id) => ({ id, label: exportFieldLabel(id) }));
    const seenLabels = new Set();
    const seenIds = new Set();
    const uniqueFields = [];
    [...configuredFields, ...detailFields]
      .filter((field) => field?.id && !["actions", "info"].includes(field.id))
      .map((field, index) => ({
        id: field.id,
        label: field.label || exportFieldLabel(field.id),
        group: field.group || (index < configuredFields.length ? "Table" : exportFieldGroup(field.id)),
      }))
      .forEach((field) => {
        const labelKey = canonicalExportFieldKey(field.label);
        const idKey = canonicalExportFieldKey(field.id);
        const duplicate = seenLabels.has(labelKey) || seenIds.has(idKey);
        if (duplicate) return;
        seenLabels.add(labelKey);
        seenIds.add(idKey);
        uniqueFields.push(field);
      });
    return uniqueExportFields(uniqueFields);
  }, [exportContext?.columns, exportContext?.fields, exportContext?.rows]);
  const [selectedExportFieldIds, setSelectedExportFieldIds] = React.useState([]);
  const normalizedRightButtons =
    Array.isArray(rightButtons) && rightButtons.length
      ? rightButtons.filter(Boolean)
      : rightButton
        ? [rightButton]
        : [];

  const closeConfirmation = React.useCallback(() => {
    if (confirmBusy) return;
    setPendingConfirmation(null);
  }, [confirmBusy]);

  const requestConfirmation = React.useCallback((descriptor) => {
    if (!descriptor || typeof descriptor.onConfirm !== "function") return false;

    setConfirmBusy(false);
    setPendingConfirmation({
      title: String(descriptor.title || "Confirm Bulk Action").trim(),
      message: String(descriptor.message || "").trim(),
      confirmLabel: String(descriptor.confirmLabel || "Confirm").trim(),
      cancelLabel: String(descriptor.cancelLabel || "Cancel").trim(),
      onConfirm: descriptor.onConfirm,
    });
    return true;
  }, []);

  const handleConfirmAction = React.useCallback(async () => {
    if (!pendingConfirmation?.onConfirm || confirmBusy) return;

    setConfirmBusy(true);
    try {
      const result = pendingConfirmation.onConfirm();
      if (result && typeof result.then === "function") {
        await result;
      }
      setPendingConfirmation(null);
    } catch (error) {
      console.error("Bulk action confirmation failed:", error);
      setPendingConfirmation(null);
    } finally {
      setConfirmBusy(false);
    }
  }, [confirmBusy, pendingConfirmation]);

  React.useEffect(() => {
    if (selectedCount > 0 || !pendingConfirmation) return;
    setPendingConfirmation(null);
    setConfirmBusy(false);
  }, [pendingConfirmation, selectedCount]);

  React.useEffect(() => {
    if (!rightButtonFeedback) return undefined;
    const timeout = window.setTimeout(() => setRightButtonFeedback(null), 2500);
    return () => window.clearTimeout(timeout);
  }, [rightButtonFeedback]);

  const notifySelectionRequired = React.useCallback((message) => {
    setSelectionNotice(String(message || `Select at least one ${selectionLabel || "item"} first.`));
  }, [selectionLabel]);

  const confirmationContextValue = React.useMemo(
    () => ({ requestConfirmation, notifySelectionRequired }),
    [notifySelectionRequired, requestConfirmation]
  );

  const openExportDialog = React.useCallback(() => {
    setSelectedExportFieldIds(exportFields.map((field) => field.id));
    setExportTab(exportFields.some((field) => field.group === "Table") ? "Table" : exportFields[0]?.group || "Details");
    setPreviewPage(0);
    setExportDialogOpen(true);
  }, [exportFields]);

  const downloadSelectedFields = React.useCallback(async () => {
    const selectedIds = new Set((exportContext?.selectedIds || []).map((id) => String(id)));
    const rows = (exportContext?.rows || []).filter((row) =>
      selectedIds.has(String(exportContext?.getRowId?.(row) ?? exportRowKey(row)))
    );
    const fields = exportFields.filter((field) => selectedExportFieldIds.includes(field.id));
    if (!rows.length || !fields.length) return;
    const csvValue = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    setExportProgress(1);
    await new Promise((resolve) => window.setTimeout(resolve, 0));
    const lines = [fields.map((field) => csvValue(field.label)).join(",")];
    const chunkSize = 250;
    for (let start = 0; start < rows.length; start += chunkSize) {
      lines.push(
        ...rows
          .slice(start, start + chunkSize)
          .map((row) => fields.map((field) => csvValue(exportFieldValue(row, field.id))).join(","))
      );
      setExportProgress(Math.min(99, Math.round(((start + chunkSize) / rows.length) * 100)));
      await new Promise((resolve) => window.setTimeout(resolve, 0));
    }
    const csv = lines.join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "export.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    setExportProgress(100);
    await new Promise((resolve) => window.setTimeout(resolve, 150));
    setExportProgress(0);
    setExportDialogOpen(false);
  }, [exportContext, exportFields, selectedExportFieldIds]);

  const renderAction = (action, index) => {
    if (!action || typeof action !== "object") return null;

    if (action.type === "divider") {
      return (
        <Divider
          key={`divider-${index}`}
          orientation="vertical"
          flexItem
          sx={{
            mx: 0.8,
            my: 0.4,
            borderColor: BORDER,
            ...(action.sx || {}),
          }}
        />
      );
    }

    if (action.type === "node") {
      return (
        <Box
          key={action.key || `node-${index}`}
          sx={{ display: "inline-flex", alignItems: "center" }}
        >
          {action.node}
        </Box>
      );
    }

    if (action.type === "menu") {
      return (
        <BulkMenuAction
          key={action.key || action.label || `menu-${index}`}
          action={action}
          actionKey={action.key || action.label || `menu-${index}`}
          requestConfirmation={requestConfirmation}
          selectedCount={selectedCount}
          notifySelectionRequired={notifySelectionRequired}
          onRequestExport={exportContext ? openExportDialog : null}
        />
      );
    }

    const isTableExport = Boolean(
      exportContext && action.useFieldPicker !== false && /export/i.test(String(action.label || ""))
    );
    const hasClickHandler = typeof action.onClick === "function" || isTableExport;
    const selectionMissing = action.requiresSelection !== false && Number(selectedCount) <= 0;
    const disabled = Boolean((action.disabled && !selectionMissing) || !hasClickHandler);
    const handleClick = (event) => {
      if (selectionMissing) {
        notifySelectionRequired(action.emptySelectionMessage);
        return;
      }
      if (disabled) return;
      if (isTableExport) {
        openExportDialog();
        return;
      }
      if (hasClickHandler) {
        event?.persist?.();
        const confirmation = buildConfirmationDescriptor({
          config: action,
          selectedCount,
          defaultEnabled: true,
          onConfirm: () => action.onClick(event, { confirmedByBulkActionsBar: true }),
        });

        if (confirmation) {
          requestConfirmation(confirmation);
          return;
        }

        action.onClick(event);
      }
    };

    return (
      <Button
        key={action.key || action.label || `button-${index}`}
        className="bulk-action-flat"
        type="button"
        variant="text"
        disableRipple={Boolean(action.disableRipple ?? false)}
        disabled={disabled}
        onClick={handleClick}
        startIcon={null}
        endIcon={action.endIcon || null}
        sx={{
          minWidth: "auto",
          p: 0,
          px: 0,
          py: 0,
          borderRadius: 999,
          textTransform: "none",
          fontSize: 14,
          fontWeight: 500,
          lineHeight: 1.2,
          color: disabled ? "#B3B8C5" : PURPLE,
          background: "transparent !important",
          backgroundColor: "transparent !important",
          border: "none !important",
          borderColor: "transparent !important",
          boxShadow: "none !important",
          justifyContent: "flex-start",
          "&:hover, &.Mui-focusVisible, &:active": {
            background: `${SOFT_PURPLE_HOVER_BG} !important`,
            backgroundColor: `${SOFT_PURPLE_HOVER_BG} !important`,
            border: "none !important",
            borderColor: "transparent !important",
            boxShadow: "none !important",
            color: disabled ? "#B3B8C5" : "#7C3AED",
          },
          "&.Mui-disabled": {
            color: "#B3B8C5",
            background: "transparent !important",
            backgroundColor: "transparent !important",
            border: "none !important",
            borderColor: "transparent !important",
            boxShadow: "none !important",
          },
          ...(action.sx || {}),
        }}
      >
        {action.label}
      </Button>
    );
  };

  const visibleExportFields = uniqueExportFields(
    exportFields.filter((field) => field.group === exportTab)
  );
  const selectedPreviewIds = new Set((exportContext?.selectedIds || []).map((id) => String(id)));
  const getPreviewRowId = (row) => exportContext?.getRowId?.(row) ?? exportRowKey(row);
  const previewRows = (exportContext?.rows || []).filter((row) =>
    selectedPreviewIds.has(String(getPreviewRowId(row)))
  );
  const previewPageSize = 5;
  const previewPageCount = Math.max(1, Math.ceil(previewRows.length / previewPageSize));
  const paginatedPreviewRows = previewRows.slice(previewPage * previewPageSize, (previewPage + 1) * previewPageSize);
  const exportTabs = ["Table", "Details", "Stages", "Matches", "Activities"];

  const renderRightButton = (buttonConfig, index) => {
    if (!buttonConfig || typeof buttonConfig !== "object") return null;

    const requiresSelection = buttonConfig.requiresSelection !== false;
    const selectionMissing = requiresSelection && Number(selectedCount) <= 0;
    const emptySelectionMessage = String(
      buttonConfig.emptySelectionMessage || `Select at least one ${selectionLabel || "record"} first.`
    ).trim();
    const resolvedBackground = buttonConfig.background || pillButtonFilledSx.backgroundColor || PURPLE;
    const resolvedDisabledBackground =
      buttonConfig.disabledBackground || resolvedBackground;
    const resolvedDisabledBorder =
      buttonConfig.disabledBorderColor || resolvedBackground;
    const resolvedTextColor = buttonConfig.color || "#fff";
    const handleClick = (event) => {
      if (selectionMissing) {
        setRightButtonFeedback({
          key: buttonConfig.key || buttonConfig.label || `right-button-${index}`,
          anchorEl: event.currentTarget,
          message: emptySelectionMessage,
        });
        return;
      }

      if (Boolean(buttonConfig.disabled) || typeof buttonConfig.onClick !== "function") return;

      event?.persist?.();
      const confirmation = buildConfirmationDescriptor({
        config: buttonConfig,
        selectedCount,
        defaultEnabled: false,
        onConfirm: () => buttonConfig.onClick(event, { confirmedByBulkActionsBar: true }),
      });

      if (confirmation) {
        requestConfirmation(confirmation);
        return;
      }

      buttonConfig.onClick(event);
    };

    return (
      <>
      <Button
        key={buttonConfig.key || buttonConfig.label || `right-button-${index}`}
        className={withPillButtonClassName(buttonConfig.className)}
        type="button"
        variant="contained"
        disableElevation
        onClick={handleClick}
        disabled={Boolean(buttonConfig.disabled) && !selectionMissing}
        aria-disabled={selectionMissing ? "true" : undefined}
        startIcon={resolveActionStartIcon(buttonConfig, 18)}
        endIcon={buttonConfig.endIcon || null}
        sx={[
          pillButtonInteractiveSx,
          {
            ...pillButtonFilledSx,
            minWidth: "auto",
            height: 40,
            minHeight: 40,
            px: 2.5,
            fontSize: 13,
            fontWeight: 400,
            color: resolvedTextColor,
            backgroundColor: selectionMissing ? resolvedDisabledBackground : resolvedBackground,
            border: `1px solid ${selectionMissing ? resolvedDisabledBorder : resolvedBackground}`,
            whiteSpace: "nowrap",
            borderRadius: 999,
            boxShadow: "none",
            "&:hover": {
              background: "#FFFFFF !important",
              backgroundColor: "#FFFFFF !important",
              color: `${resolvedBackground} !important`,
              borderColor: `${resolvedBackground} !important`,
              boxShadow: "none !important",
            },
            "&:active": {
              background: "#FFFFFF !important",
              backgroundColor: "#FFFFFF !important",
              color: `${resolvedBackground} !important`,
              borderColor: `${resolvedBackground} !important`,
              transform: "translateY(1px)",
              boxShadow: "none !important",
            },
            "&:focus-visible, &.Mui-focusVisible": {
              outline: `3px solid ${buttonConfig.background || PURPLE}`,
              outlineOffset: 2,
              boxShadow: "0 0 0 1px rgba(255,255,255,0.54) inset",
            },
            "&.Mui-disabled": {
              color: resolvedTextColor,
              backgroundColor: resolvedDisabledBackground,
              borderColor: resolvedDisabledBorder,
              boxShadow: "none",
              opacity:
                typeof buttonConfig.disabledOpacity === "number"
                  ? buttonConfig.disabledOpacity
                  : 0.45,
            },
          },
          buttonConfig.sx || {},
        ]}
      >
        {buttonConfig.label}
      </Button>
      <Popper
        open={Boolean(
          rightButtonFeedback?.message &&
            rightButtonFeedback?.key === (buttonConfig.key || buttonConfig.label || `right-button-${index}`)
        )}
        anchorEl={rightButtonFeedback?.anchorEl}
        placement="top"
        transition
        modifiers={[{ name: "offset", options: { offset: [0, 10] } }]}
        sx={{ zIndex: (theme) => theme.zIndex.snackbar }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={180}>
            <Alert severity="info" onClose={() => setRightButtonFeedback(null)}>
              {rightButtonFeedback?.message || ""}
            </Alert>
          </Fade>
        )}
      </Popper>
      </>
    );
  };

  return (
    <BulkActionsBarContext.Provider value={confirmationContextValue}>
      <Box>
        <Snackbar
          open={Boolean(selectionNotice)}
          autoHideDuration={3000}
          onClose={() => setSelectionNotice("")}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity="info" variant="filled" onClose={() => setSelectionNotice("")}>
            {selectionNotice}
          </Alert>
        </Snackbar>
        {pendingConfirmation?.message ? (
          <Fade in timeout={180}>
            <Box
              sx={{
                mb: 1,
                px: 1.5,
                py: 1.2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                flexWrap: "wrap",
                border: "1px solid #FDE68A",
                borderRadius: 2.5,
                backgroundColor: "#FFFBEB",
                boxShadow: "0 10px 24px rgba(15,23,42,0.08)",
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#92400E",
                    mb: 0.25,
                    whiteSpace: "nowrap",
                  }}
                >
                  {pendingConfirmation.title || "Confirm Bulk Action"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    lineHeight: 1.45,
                    color: "#7C2D12",
                  }}
                >
                  {pendingConfirmation.message}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.8,
                  flexShrink: 0,
                }}
              >
                <Button
                  type="button"
                  variant="text"
                  onClick={closeConfirmation}
                  disabled={confirmBusy}
                  sx={{
                    minWidth: "auto",
                    px: 1.5,
                    py: 0.6,
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: "none",
                    color: "#7C2D12",
                  }}
                >
                  {pendingConfirmation.cancelLabel || "Cancel"}
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  disableElevation
                  onClick={handleConfirmAction}
                  disabled={confirmBusy}
                  sx={{
                    minWidth: "auto",
                    px: 1.75,
                    py: 0.65,
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: "none",
                    color: "#fff",
                    backgroundColor: PURPLE,
                    "&:hover": {
                      backgroundColor: PURPLE,
                    },
                  }}
                >
                  {confirmBusy ? "Working..." : pendingConfirmation.confirmLabel || "Confirm"}
                </Button>
              </Box>
            </Box>
          </Fade>
        ) : null}

        <Box
          className="bulk-actions-row"
          sx={{
            width: "100%",
            px: 1.5,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 1.5,
            flexWrap: "nowrap",
            overflowX: "auto",
            overflowY: "hidden",
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": {
              height: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#D0D5DD",
              borderRadius: 999,
            },
            backgroundColor: "#fff",
            ...sx,
          }}
        >
          <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          gap: 1,
          flexWrap: "nowrap",
          minWidth: "max-content",
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 500,
            lineHeight: 1.2,
            color: MUTED,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          Bulk actions:
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 0.8,
            flexWrap: "nowrap",
            minWidth: "max-content",
          }}
        >
          {actions.map((action, index) => renderAction(action, index))}
        </Box>

        {selectedCount > 0 ? (
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 500,
              color: TEXT,
              ml: 0.5,
              whiteSpace: "nowrap",
            }}
          >
            ({selectedCount} selected)
          </Typography>
        ) : null}
          </Box>

          {normalizedRightButtons.length ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1,
                flexWrap: "nowrap",
                flexShrink: 0,
                ml: "auto",
                minWidth: "max-content",
              }}
            >
              {normalizedRightButtons.map((buttonConfig, index) => renderRightButton(buttonConfig, index))}
            </Box>
          ) : null}
        </Box>
      </Box>
      <Dialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        fullWidth
        maxWidth="md"
        scroll="body"
        PaperProps={{ sx: { width: 840, maxWidth: "calc(100vw - 32px)", height: "fit-content", maxHeight: "calc(100vh - 64px)", overflowY: "hidden !important" } }}
      >
        <DialogTitle data-popup-header sx={{ fontSize: 18, fontWeight: 700 }}>Export fields</DialogTitle>
        <DialogContent sx={{ display: "block", flex: "none !important", overflow: "visible !important", minHeight: "0 !important", pt: "12px !important", pb: 1.5 }}>
          <Typography sx={{ color: MUTED, fontSize: 13, mb: 1 }}>Choose the fields to include in the CSV.</Typography>
          {exportProgress > 0 ? <Box sx={{ mb: 1.25 }}><LinearProgress variant="determinate" value={exportProgress} /><Typography sx={{ fontSize: 12, color: MUTED, mt: 0.5 }}>Creating CSV… {exportProgress}%</Typography></Box> : null}
          <Tabs
            value={exportTab}
            onChange={(_event, value) => setExportTab(value)}
            variant="fullWidth"
            sx={{ minHeight: 38, mb: 1, borderBottom: `1px solid ${BORDER}`, "& .MuiTab-root": { minHeight: 38, minWidth: 0, px: 0.75, textTransform: "none", fontSize: 12.5, whiteSpace: "nowrap" } }}
          >
            {exportTabs.map((tab) => <Tab key={tab} value={tab} label={`${tab} (${exportFields.filter((field) => field.group === tab).length})`} />)}
          </Tabs>
          {visibleExportFields.length ? (
            <>
              <TableContainer
                sx={{
                  // Do not reserve a viewport-sized area for a one-row preview.
                  // Its height follows the actual rows; only longer selections
                  // become scrollable.
                  maxHeight: 310,
                  overflow: "auto",
                  border: `1px solid ${BORDER}`,
                  borderRadius: 1,
                  flex: "0 0 auto",
                }}
              >
                <Table
                  size="small"
                  sx={{
                    minWidth: Math.max(720, visibleExportFields.length * 180),
                    tableLayout: "fixed",
                    "& .MuiTableCell-root": { boxSizing: "border-box", lineHeight: 1.35 },
                    "& .MuiTableHead-root .MuiTableCell-root": { height: 42 },
                    "& .MuiTableBody-root .MuiTableRow-root": { height: "auto !important" },
                    "& .MuiTableBody-root .MuiTableCell-root": { height: 38 },
                  }}
                >
                  <TableHead><TableRow>{visibleExportFields.map((field) => <TableCell key={field.id} sx={{ minWidth: 180, py: 0.35, px: 1, backgroundColor: "#F9FAFB" }}><Box sx={{ display: "flex", alignItems: "center", gap: 0.25, minHeight: 30 }}><Checkbox size="small" checked={selectedExportFieldIds.includes(field.id)} onChange={() => setSelectedExportFieldIds((prev) => prev.includes(field.id) ? prev.filter((id) => id !== field.id) : [...prev, field.id])} />{field.label}</Box></TableCell>)}</TableRow></TableHead>
                  <TableBody>{paginatedPreviewRows.length ? paginatedPreviewRows.map((row, rowIndex) => <TableRow key={getPreviewRowId(row) || rowIndex}>{visibleExportFields.map((field) => <TableCell key={field.id} sx={{ minWidth: 180, py: 0.55, px: 1, verticalAlign: "top" }}><Typography noWrap title={exportFieldValue(row, field.id) || "No preview available"} sx={{ color: MUTED, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis" }}>{exportFieldValue(row, field.id) || "No preview available"}</Typography></TableCell>)}</TableRow>) : <TableRow><TableCell colSpan={visibleExportFields.length} sx={{ py: 1, px: 1.25, color: MUTED, fontSize: 12 }}>Select one or more records to preview their export values.</TableCell></TableRow>}</TableBody>
                </Table>
              </TableContainer>
              {previewRows.length > previewPageSize ? <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, mt: 1 }}><Button size="small" disabled={!previewPage} onClick={() => setPreviewPage((value) => value - 1)}>Prev</Button><Typography sx={{ fontSize: 12, color: MUTED }}>{previewPage + 1} / {previewPageCount}</Typography><Button size="small" disabled={previewPage >= previewPageCount - 1} onClick={() => setPreviewPage((value) => value + 1)}>Next</Button></Box> : null}
            </>
          ) : <Typography sx={{ color: MUTED, fontSize: 12.5, py: 1 }}>No exportable fields are available for this tab.</Typography>}
        </DialogContent>
        <DialogActions data-popup-footer sx={{ px: 3, pb: 2 }}>
          <Button disabled={exportProgress > 0} onClick={() => setExportDialogOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" disabled={!selectedExportFieldIds.length || exportProgress > 0} onClick={downloadSelectedFields} sx={{ textTransform: "none", backgroundColor: PURPLE, "&:hover": { backgroundColor: PURPLE } }}>{exportProgress > 0 ? "Creating…" : "Export CSV"}</Button>
        </DialogActions>
      </Dialog>
    </BulkActionsBarContext.Provider>
  );
}
